import React from 'react';
import { Users, Lightbulb, MessageCircle, Presentation, Coffee, Sparkles, Trophy } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { useLanguage } from '../context/LanguageContext';

const EventsPage = () => {
  const { t } = useLanguage();

  const eventConfig = [
    { key: 'seminars', icon: Presentation, color: '#4DFF88' },
    { key: 'workshops', icon: Lightbulb, color: '#4DD6FF' },
    { key: 'conferences', icon: Users, color: '#FF4DA6' },
    { key: 'visionMeetings', icon: Coffee, color: '#4DFF88' },
    { key: 'socialEvents', icon: MessageCircle, color: '#4DD6FF' },
    { key: 'competitions', icon: Trophy, color: '#FF4DA6' },
  ];

  return (
    <div className="min-h-screen bg-[#060b1a]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#060b1a] via-[#0a1628] to-[#0f1f33]" />
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-[#FF4DA6]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-[#4DFF88]/10 rounded-full blur-[120px]" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full border border-[#FF4DA6]/30 bg-[#FF4DA6]/5 text-[#FF4DA6] text-sm font-medium mb-6">
              {t('events.badge')}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
              {t('events.title')}
            </h1>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative py-16 bg-[#060b1a]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Introduction */}
          <div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm mb-16">
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              {t('events.intro1')}
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              {t('events.intro2')}
            </p>
            <p className="text-lg text-gray-300 leading-relaxed">
              {t('events.intro3')}
            </p>
          </div>

          {/* Event Types */}
          <div className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-8 text-center">
              {t('events.typesTitle')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {eventConfig.map((event, index) => (
                <div
                  key={index}
                  className="group p-6 rounded-xl border border-white/10 bg-white/5 hover:border-white/20 transition-all duration-300"
                >
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: `${event.color}15` }}
                  >
                    <event.icon className="w-7 h-7" style={{ color: event.color }} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {t(`events.${event.key}.title`)}
                  </h3>
                  <p className="text-gray-400">
                    {t(`events.${event.key}.desc`)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Vision Card */}
          <div className="p-8 rounded-2xl border border-[#4DFF88]/30 bg-gradient-to-br from-[#4DFF88]/10 to-transparent text-center">
            <Sparkles className="w-12 h-12 text-[#4DFF88] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-4">{t('events.visionTitle')}</h3>
            <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
              <span className="text-[#4DFF88] font-semibold">{t('events.visionText')}</span>
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EventsPage;
